/* eslint-disable react/forbid-prop-types */
import { useState } from 'react';

import {
  useButtonPressedCallback,
  useClickstreamCallback
} from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Checkbox from '@zenbusiness/zui/Checkbox';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';

import LockSharp from '@mui/icons-material/LockSharp';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import { getCountryOptions } from '#client/utils/country';

import Ssn from './Ssn';

/**
 *When user chooses option entering passport number, this mean user is not from US,
 *so we will remove US from countries list
 */
const passportOptions = getCountryOptions({ filterByCode: 'US' });

export const SsnUIField = ({
  value = {},
  dataChangeHandler = () => {},
  field = { setSaved: () => {}, setLoading: () => {} },
  disabled
}) => {
  const [values, setValues] = useState({
    ssn: value?.ssnLast ? `*****${value?.ssnLast}` : '',
    noSsn: !value?.ssnLast && !!value?.nationality && !!value?.passport,
    nationality: passportOptions.find((option) => option.code === value?.nationality) ?? null,
    passport: value?.passport ?? '',
    showSsn: !!value?.ssnLast,
    valid: !!value?.ssnLast || !!(value?.nationality && value?.passport)
  });

  const trackSsnUiChangeClick = useClickstreamCallback('buttonPressed');
  const onDataChanged = (newValues) => {
    setValues(() => newValues);

    const { ssn } = newValues;
    const ssnField = { ssn };
    const noSsnField = { ssn: null };

    /**
     * We need to clear EDD data if user changes to use SSN
     */
    const dataFields = newValues.noSsn
      ? {
          bankAccountMembers: [
            {
              ...noSsnField,
              nationality: newValues.nationality?.code,
              passport: newValues.passport
            }
          ]
        }
      : {
          bankAccountMembers: [ssnField]
        };

    const trackingValues =
      newValues.noSsn && newValues.passport
        ? {
            elementName: 'banking_collect_info',
            elementText: 'passport_number',
            appPageName: 'banking_application'
          }
        : {
            elementName: 'banking_collect_info',
            elementText: 'ssn_entry',
            appPageName: 'banking_application'
          };

    const ssnValue = ssn;

    const valid =
      newValues.valid &&
      (ssnValue || (newValues.noSsn && newValues.nationality && newValues.passport));

    if (valid) {
      field.setLoading();
      /**
       * Tracking Details: Tracking to be added to the banking application Collect Info screen.
       * Question:
       *          Where are customers typically dropping off on this screen?
       *          Do they typically drop off at SSN?
       *          How often are they viewing the user agreements?
       */
      trackSsnUiChangeClick(trackingValues);
    }

    dataChangeHandler(
      valid,
      dataFields,
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError, valid);
        forceUpdate();
      },
      field.key
    );
  };

  const handleSsnChange = (event) => {
    const newValue = event.target.value;
    const newValues = {
      ...values,
      valid: /^\d{9}$/.test(newValue),
      ssn: newValue
    };
    onDataChanged(newValues);
  };

  const trackSsnUiNoChangeClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    elementText: 'no_ssn_checkbox',
    appPageName: 'banking_application'
  });
  const handleNoSsnChange = () => {
    const ssnValue = { ssn: '' };
    const newValues = {
      ...values,
      ...ssnValue,
      nationality: null,
      passport: null,
      noSsn: !values.noSsn,
      valid: false
    };
    onDataChanged(newValues);
    /**
     * Tracking Details: Tracking to be added to the banking application Collect Info screen.
     * Question:
     *          Where are customers typically dropping off on this screen?
     *          Do they typically drop off at SSN?
     *          How often are they viewing the user agreements?
     */
    trackSsnUiNoChangeClick();
  };

  const trackSsnUiChangeNationalityClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    elementText: 'country_of_issuance',
    appPageName: 'banking_application'
  });
  const handleChangeNationality = (event, nationality) => {
    const newValues = {
      ...values,
      nationality,
      valid: !!(nationality && values.passport)
    };
    onDataChanged(newValues);
    /**
     * Tracking Details: Tracking to be added to the banking application Collect Info screen.
     * Question:
     *          Where are customers typically dropping off on this screen?
     *          Do they typically drop off at SSN?
     *          How often are they viewing the user agreements?
     */
    trackSsnUiChangeNationalityClick();
  };

  const handleChangePassport = (event) => {
    const passport = event.target.value;
    const newValues = {
      ...values,
      passport,
      valid: !!(passport && values.nationality)
    };

    dataChangeHandler(false, {}, (forceUpdate, { isSave, isError }) => {
      field.setSaved(isSave, isError, false);
      forceUpdate();
    });
    setValues(newValues);
  };

  const handleBlur = () => {
    onDataChanged(values);
  };

  const handleClickShowSsn = () => {
    setValues({
      ...values,
      showSsn: !values.showSsn
    });
  };

  return (
    <Stack spacing="8px">
      <Ssn
        disabled={disabled}
        handleChange={handleSsnChange}
        handleShow={handleClickShowSsn}
        values={values ?? ''}
      />
      <Stack
        alignItems="center"
        direction="row"
        paddingLeft="10px"
        spacing="4px"
      >
        <LockSharp
          color="cyan.main"
          fontSize="small"
        />
        <Text color="tertiary">Encrypted using 256-BIT SSL</Text>
      </Stack>
      <Box marginTop="24px">
        <FormControlLabel
          control={
            <Checkbox
              checked={values.noSsn ?? ''}
              color="default"
              disabled={disabled}
              id="noSsn"
              onChange={handleNoSsnChange}
            />
          }
          label={<Text size="xl">I don’t have a Social Security number</Text>}
          margin="normal"
        />
      </Box>
      {values.noSsn && (
        <Stack
          marginTop="32px!important"
          paddingLeft="40px"
          spacing="24px"
        >
          <Text
            size="xl"
            weight="medium"
          >
            What’s your passport info?
          </Text>
          <FormControl
            disabled={disabled}
            fullWidth
          >
            <AutoCompleteInput
              blurOnSelect
              disabled={disabled}
              id="country"
              onChange={handleChangeNationality}
              openOnFocus
              options={passportOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={params.inputProps}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{ ...params.InputProps }}
                  label="Country of Issuance"
                />
              )}
              value={values.nationality ?? null}
            />
          </FormControl>
          <TextField
            disabled={disabled}
            fullWidth
            id="passport"
            label="Passport Number"
            onBlur={handleBlur}
            onChange={handleChangePassport}
            value={values.passport ?? ''}
          />
        </Stack>
      )}
    </Stack>
  );
};

SsnUIField.propTypes = {
  value: PropTypes.object,
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool
};
